/**
 * HSL colour helpers for the theme editor.
 *
 * The control plane stores theme colours as bare space-separated HSL
 * ("222.2 47.4% 11.2%", optionally "0 0% 100% / 10%") and rejects anything
 * else with a 422 (`control_plane/schemas/theme.py::validate_hsl`). Colour
 * pickers commonly copy "9° 82% 57%" or "hsl(9, 82%, 57%)", so the editor
 * normalises those shapes and validates with the same pattern before saving.
 */

import type { ThemeColorMode } from "@/services/api/theme";

// Mirrors the backend regex exactly — keep the two in step.
const HSL_PATTERN =
  /^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%(\s+\/\s+\d+(\.\d+)?%)?$/;

export function isValidHsl(value: string): boolean {
  return HSL_PATTERN.test(value);
}

/** Rewrite common colour-picker spellings into the backend's HSL format. */
export function normaliseHsl(value: string): string {
  let v = value.trim();
  const fn = v.match(/^hsla?\((.*)\)$/i);
  if (fn) v = fn[1];
  return v
    .replace(/°|deg/gi, "")
    .replace(/,/g, " ")
    .replace(/\s*\/\s*/, " / ")
    .replace(/\s+/g, " ")
    .trim();
}

export function normaliseThemeColors(colors: ThemeColorMode): ThemeColorMode {
  const out = { ...colors };
  for (const key of Object.keys(out) as (keyof ThemeColorMode)[]) {
    const value = out[key];
    if (typeof value === "string") out[key] = normaliseHsl(value);
  }
  return out;
}

/** Keys whose value the backend would reject. */
export function invalidColorKeys(colors: ThemeColorMode): (keyof ThemeColorMode)[] {
  return (Object.keys(colors) as (keyof ThemeColorMode)[]).filter((key) => {
    const value = colors[key];
    return typeof value === "string" && !isValidHsl(value);
  });
}

/**
 * Fill keys a stored theme predates (e.g. chart-1..5) from the defaults, so a
 * save always sends every field the backend requires.
 */
export function withDefaultColors(
  colors: Partial<ThemeColorMode> | null | undefined,
  defaults: ThemeColorMode,
): ThemeColorMode {
  return { ...defaults, ...(colors ?? {}) };
}
